<script>
  import Popover from "#lib/Popover.svelte";
  import t from "#lib/i18n.svelte.js";

  let {
    /** @type {any} Content to be rendered inside the component. */
    children,
    /** @type {any} Message to be displayed in the popover. */
    message,
    /** @type {Array<string>} Accepted file types as MIME types */
    accept = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
    /** @type {Function(File):void} Callback function to handle a single dropped file. */
    handle_file_drop,
    /** @type {Function(File[]):void} Callback for multi-file drops (enables multiple mode). */
    handle_files_drop,
    /** @type {boolean} True if the user starts dragging a file into the drop zone. */
    is_dragging = false,
    /** @type {boolean} enable or disable the drag & drop (e.g. in edit mode) */
    enabled = true,
    /** @type {boolean} Uses the entire window as a drop zone */
    drop_on_window = true,
  } = $props();
  
  let is_valid_file = $state(true);

  let popover_ref = $state();
  let drop_zone = $state();

  function on_drag_over(event) {
    if (!enabled) return;
    if (is_target_valid(event)) {
      event.preventDefault();
      is_dragging = true;
    }
  };

  function on_drag_enter(event) {
    if (!enabled) return;
    if (is_target_valid(event)) {
      popover_ref.showPopover();
    }
  };

  
  function on_drag_leave(event) {
    if (!enabled) return;
    // Check if the drag is leaving the drop zone or just a child element within it
    if (is_target_valid(event)) return;

    // reset
    is_dragging = false;
    popover_ref.hidePopover();
  };

  function on_drop(event) {
    if (!enabled) return;
    if (!is_target_valid(event)) return;

    event.preventDefault();

    // Multi-file mode: hand over every valid file from the drop
    if (handle_files_drop) {
      const dropped_files = Array.from(event.dataTransfer.files || [])
        .filter(file => check_file_type(file));
      is_valid_file = dropped_files.length > 0;
      if (is_valid_file) {
        handle_files_drop(dropped_files);
        popover_ref.hidePopover();
      }
      is_dragging = false;
      return;
    }

    const dropped_file = event.dataTransfer.files?.[0] || event.dataTransfer.items?.[0]?.getAsFile();

    is_valid_file = check_file_type(dropped_file);
    if (!is_valid_file) {
      is_dragging = false;
      return;
    }

    handle_file_drop && handle_file_drop(dropped_file);

    // reset
    is_dragging = false;
    popover_ref.hidePopover();
  };

  function is_target_valid(event) {
    return drop_on_window || event.target === drop_zone || drop_zone.contains(event.relatedTarget);
  };

  function check_file_type(file) {
    return file && accept.includes(file.type);
  };
</script>


<svelte:window
  ondragover={on_drag_over}
  ondragenter={on_drag_enter}
  ondragleave={on_drag_leave}
  ondrop={on_drop}
/>

<div 
  bind:this={drop_zone} 
  class="drag-and-drop-uploader"
  class:is_dragging
>
  {#if children}
    {@render children()}
  {/if}

  <!-- TODO: Create a Toast Component that leverages Popover for ephermal messages -->
  <Popover
    anchor={drop_zone}
    bind:ref={popover_ref}
    class="normal"
    placement="center"
  >
    <div class="flex-column p-6 px py g-3 gap-y">
      {#if is_valid_file}
        {#if message}
          {@render message()}
        {:else}
          <p>{t("drop_files_to_upload")}</p>
        {/if}
      {:else}
        <p>{t("invalid_file_type")}</p>
        <p>{t("supported_file_types_are")}:</p>
        <p class="-caps">
          {#each accept as file_type,i}
            {file_type.replace(/.*\//g,'')}{i < accept.length - 1 ? ', ' : ''}
          {/each}
        </p>
      {/if}
    </div>    
  </Popover>
</div>

<style lang="postcss">
  .is_dragging {
    position: relative;
    &:before {
      content: "";
      position: absolute;
      inset: 0 var(--page-inline-spacing);
      width: calc( 100% - var(--page-inline-spacing) * 2);
      height: 100%;
      box-shadow: var(--selected-border);
      background-color: var(--color-default-background);
      border-radius: var(--radius-md);
      z-index: 10;
    }
  }
</style>